import { WorkbenchDto } from '@/services/dtos'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Box, Button, Dialog, DialogContent, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SettingsIcon from '@mui/icons-material/Settings'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import WorkbenchForm from '../forms/WorkbenchForm'
import { getWorkbenches, selectCurrentWorkbenchId, setCurrentWorkbenchId } from '@state/reducers/workbenchReducer'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { WorkbenchSelector } from './WorkbenchSelector'
import { UpTransition } from '../animations'
import WorkbenchGeneratorInstanceForm from '../forms/WorkbenchGeneratorInstanceForm'

interface WorkbenchSelectorDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
}

export function WorkbenchSelectorDialog({ open, setOpen }: WorkbenchSelectorDialogProps) {
  const dispatch = useAppDispatch()

  return (<Dialog 
    open={open} 
    maxWidth="md" 
    fullWidth 
    onClose={() => setOpen(false)}>
    <WorkbenchSelector 
      onSubmit={(workbench) => {
        workbench && dispatch(setCurrentWorkbenchId(workbench.id))
        setOpen(false)
      }}
      onClose={() => setOpen(false)}
    />
  </Dialog>)
}

interface WorkbenchCardProps {
  workbench?: WorkbenchDto | null
  withActions?: boolean
  selected?: boolean
  onSelect?: () => void
}

export function WorkbenchCard({ workbench, withActions = false, selected, onSelect }: WorkbenchCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClose = () => setAnchorEl(null)
  const theme = useTheme()
  const [openForm, setOpenForm] = useState(false)
  const [openWorkbenchSelector, setOpenWorkbenchSelector] = useState(false)
  const [openGeneratorInstanceConfig, setOpenGeneratorInstanceConfig] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const currentWorkbenchId = useAppSelector(selectCurrentWorkbenchId)
  
  if (!workbench) {
    return (
      <>
        <Typography variant="body2" color="text.secondary" className="!mb-2">
          Un poste de travail est requis pour effectuer des opérations.
        </Typography>
        <Button
          onClick={() => setOpenWorkbenchSelector(true)}
          size="small"
          variant="contained"
          color="primary">
          Choisir une enceinte blindée
        </Button>
        <WorkbenchSelectorDialog open={openWorkbenchSelector} setOpen={setOpenWorkbenchSelector} />
      </>
    )
  }

  const generatorOccupiedSlots = workbench.generatorSlots.filter(({ generatorInstance }) => generatorInstance).length

  return (
    <div 
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border bg-white w-fit transition-all
        ${onSelect ? 'cursor-pointer hover:border-gray-300' : ''}`}
      style={selected ? {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}0d`,
        boxShadow: `0 1px 2px ${theme.palette.primary.main}33`,
      } : { borderColor: '#e5e7eb' }}
      onClick={onSelect}>
      <div className="mr-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-800">{workbench.name}</span>
          {currentWorkbenchId === workbench.id && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700 leading-snug">
              En cours
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 -mt-0.5">{workbench.room?.label}</div>
      </div>
      <div className="w-px h-7 bg-gray-200 mx-1" />
      <Tooltip title={`${generatorOccupiedSlots} générateur${generatorOccupiedSlots > 0 ? 's' : ''} sur ${workbench.generatorSlots.length} emplacement${workbench.generatorSlots.length > 1 ? 's' : ''}`} arrow>
        <span className="flex items-center gap-1 text-sm text-gray-600">
          <HubOutlinedIcon sx={{ fontSize: 16 }} />
          {generatorOccupiedSlots}/{workbench.generatorSlots.length}
        </span>
      </Tooltip>
      {workbench.generatorSlots.length > 0 && (
        <div className="flex items-center gap-0.5">
          {workbench.generatorSlots.map(slot => (
            <Tooltip
              key={slot.id}
              title={slot.generatorInstance ? `${slot.name} — En service` : `${slot.name} — Vide`}
              arrow
            >
              <span
                className={`w-3 h-3 rounded-sm inline-block ${
                  slot.generatorInstance ? 'bg-gray-800' : 'bg-gray-300'
                }`}
              />
            </Tooltip>
          ))}
        </div>
      )}
      <Tooltip title={`${workbench.hotLabEquipments.length} matériel${workbench.hotLabEquipments.length > 1 ? 's' : ''} de laboratoire`} arrow>
        <span className="flex items-center gap-1 text-sm text-gray-600">
          <ScienceOutlinedIcon sx={{ fontSize: 16 }} className="text-gray-500" />
          {workbench.hotLabEquipments.length}
        </span>
      </Tooltip>
      <Tooltip title={`${workbench.wasteContainers.length} conteneur${workbench.wasteContainers.length > 1 ? 's' : ''} de déchets`} arrow>
        <span className="flex items-center gap-1 text-sm text-gray-600">
          <DeleteOutlineIcon sx={{ fontSize: 16 }} className="text-gray-500" />
          {workbench.wasteContainers.length}
        </span>
      </Tooltip>

      {withActions && <IconButton
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        className="!text-gray-400 hover:!text-gray-600"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2, mt: 0.5 } }}
      >
        <MenuItem onClick={() => { handleClose(); setOpenWorkbenchSelector(true) }}>
          <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Changer de poste de travail" primaryTypographyProps={{ fontSize: 14 }} />
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); setOpenForm(true) }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={`Modifier ${workbench.name}`} primaryTypographyProps={{ fontSize: 14 }} />
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); setOpenGeneratorInstanceConfig(true) }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={`Modifier les générateurs de ${workbench.name}`} primaryTypographyProps={{ fontSize: 14 }} />
        </MenuItem>
      </Menu>

      <Dialog 
        open={openForm} 
        maxWidth="md" 
        fullWidth 
        onClose={() => setOpenForm(false)}>
        <WorkbenchForm 
          editingWorkbench={workbench} 
          onClose={() => {
            dispatch(getWorkbenches())
            setOpenForm(false)
          }}
        />
      </Dialog>
      <WorkbenchSelectorDialog open={openWorkbenchSelector} setOpen={setOpenWorkbenchSelector} />
      <Dialog 
        fullWidth
        maxWidth="sm"
        open={openGeneratorInstanceConfig}
        TransitionComponent={UpTransition}
        onClose={() => setOpenGeneratorInstanceConfig(false)}>
        <DialogContent>
          <WorkbenchGeneratorInstanceForm
            workbench={workbench}
            onClose={() => setOpenGeneratorInstanceConfig(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
